import React, { ReactNode, useEffect, useState } from 'react';
import { useSubscription } from '@apollo/client';
import {
  ButtonIcon,
  FlexContainer,
  Icon,
  LabelBase,
  TableLegacy,
  useTimezone,
} from '@facephi/ui-react';
import { useTranslation } from 'react-i18next';
import { useEmptyState } from '../../hooks';
import { useBehaviour } from '../../providers/BehaviourProvider';
import { SessionOperation } from '../../state/model/operation';
import { getOperations } from '../../state/queries';
import { ListOperationTableProps } from './model';

const LabelRisk = ({ risk }: { risk: number }) => {
  return (
    <LabelBase
      label={String(risk)}
      color={
        risk <= 25
          ? 'green'
          : risk > 25 && risk <= 50
          ? 'blue'
          : risk > 50 && risk <= 75
          ? 'yellow'
          : 'error'
      }
    />
  );
};

const LabelAlerts = ({ count }: { count: number }) => {
  return <LabelBase label={String(count)} color={!count ? 'green' : 'error'} />;
};

const Source = ({
  source,
  children,
}: {
  source: string;
  children: ReactNode;
}) => {
  return (
    <FlexContainer columnGap="2" alignItems="center">
      <Icon
        size="24"
        iconName={source === 'Mobile' ? 'DeviceMobile' : 'Desktop'}
      />
      {children}
    </FlexContainer>
  );
};

export const ListOperationTable = ({ filters }: ListOperationTableProps) => {
  const { formatTimezone } = useTimezone();
  const { t } = useTranslation();
  const { companyId, onDetail } = useBehaviour();

  const [data, setData] = useState([]);

  const { data: { session } = {} } = useSubscription<{
    session: SessionOperation;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }>(getOperations as any, {
    variables: {
      companyId,
      search: Object.keys(filters).length ? filters : null,
    },
  });

  useEffect(() => {
    setData([]);
  }, [filters]);

  const columns = React.useMemo(
    () => [
      {
        Header: t('Risk'),
        accessor: 'risk',
        maxWidth: 100,
        style: { paddingLeft: '1rem' },
      },
      {
        Header: t('Alerts'),
        accessor: 'alertsCount',
        maxWidth: 100,
      },
      {
        Header: t('Date'),
        accessor: 'registerDate',
      },
      {
        Header: t('Session ID'),
        accessor: 'sessionId',
      },
      {
        Header: t('Geolocation'),
        accessor: 'geolocation',
      },
      {
        Header: t('Source'),
        accessor: 'source',
      },
      {
        Header: t('IP'),
        accessor: 'ip',
      },
      {
        Header: t('Action'),
        accessor: 'action',
        maxWidth: 100,
      },
    ],
    []
  );

  useEffect(() => {
    if (session) {
      const network = session.networks[0];
      const device = session.devices[0];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const newData: any = [
        {
          risk: <LabelRisk risk={session.score} />,
          alertsCount: <LabelAlerts count={session.alerts.length} />,
          registerDate: formatTimezone(
            session.registerDate,
            'DD/MM/YYYY HH:mm:ss'
          ),
          sessionId: session.sessionId,
          geolocation: `${network.geolocation.country}, ${network.geolocation.region}, ${network.geolocation.city}`,
          source: <Source source={device.family}>{t(device.family)}</Source>,
          ip: network.ip,
          action: (
            <FlexContainer
              alignItems="center"
              justifyContent="flex-start"
              flex="1"
            >
              <ButtonIcon
                iconName="Eye"
                variant="text"
                onClick={() => onDetail?.(session.sessionId)}
              />
            </FlexContainer>
          ),
        },
      ];
      setData((prevData) => newData.concat(prevData));
    }
  }, [session]);

  const { emptyState } = useEmptyState({
    data,
    isFilter: Boolean(data.length),
  });

  return (
    <TableLegacy
      columns={columns}
      data={data}
      emptyStateData={emptyState}
      hasMore={false}
    />
  );
};
